import React, { useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { API_URL } from "utils/constants";
import { noop } from "utils/common";

function defaultThumbnailNameSelector(card){
	/*
		Some video card formats have an `image`, and some have a `thumbnail`, which both
		represent the thumbnail.

		They're all objects with the same expected format:

			{
				id,
				name,
				src,
			}
	*/
	const { image, thumbnail } = card;
	return (image || thumbnail)?.name;
}

export default function CardPreview(props){
	const {
		card,
		draggable,
		isSelected,
		hasError,
		onClick,
		libraryId,
		thumbnailNameSelector, /*
			Displaying images from our API requires a `name` that's present on image objects.

			Where this lives on card objects can vary, so you may pass in this prop,
			which is a function that takes the `card` object, and returns the value
			for the image name.

			e.g. If the card is structured with a preferred thumbnail and a fallback:
			```
			{
				images: {
					primary: {
						name: "image1.png",
					},
					secondary: {
						name: "image2.png",
					}
				}
			}
			```

			You can pass in a function like:

			```
				<CardPreview
					thumbnailNameSelector = {card => card?.images?.primary?.name || card?.images?.secondary?.name}
				/>
			```

			And if either image has been specified for the card, that image's name will be used.
		*/
	} = props;

	const {
		type,
		video,
		order,
	} = card;

	const draggableRef = useCallback((handle) => {
		if(handle === null || !draggable){ return; }
		handle.draggable = true;

		const dragHandler = (e) => {
			e.dataTransfer.dropEffect = "move";
			e.dataTransfer.setData("order", order);
		};
		handle.addEventListener("dragstart", dragHandler);
	}, [draggable, order]);

	const name = thumbnailNameSelector(card);
	const src = !!name ? `${API_URL}/crm/v1/creative-libraries/${libraryId}/images/show/${name}` : null;

	let content = order + 1;

	const hasThumbnail = !!src;
	const hasVideoWithoutThumb = (type === "video" && video && !src); /*
		We only show the icon if a video has been selected for a video card, but no
		thumbnail.  If they haven't selected a video at all, then we just show the index.
	*/

	if(src){
		content = <img src={src} />;
	} else if(hasVideoWithoutThumb){
		content = (
			<>
				<i className="fas fa-video" />
				<i className="fas fa-check" />
			</>
		);
	}

	return (
		<div
			className={classNames({
				"carousel-card-preview": true,
				"no-thumbnail": !hasThumbnail && !hasVideoWithoutThumb,
				"video-no-thumb": hasVideoWithoutThumb,
				"is-selected": isSelected,
				"has-error": hasError,
			})}
			onClick={onClick}
		>
			<div ref={draggableRef}>
				{content}
			</div>
		</div>
	);
}

CardPreview.displayName = "CardPreview";

CardPreview.propTypes = {
	card: PropTypes.shape({
		type: PropTypes.string,
		image: PropTypes.shape({
			src: PropTypes.string,
		}),
		thumbnail: PropTypes.shape({
			src: PropTypes.string,
		}),
		video: PropTypes.object,
		order: PropTypes.number,
	}),
	draggable: PropTypes.bool,
	isSelected: PropTypes.bool,
	hasError: PropTypes.bool,
	onClick: PropTypes.func,
	thumbnailNameSelector: PropTypes.func,
};

CardPreview.defaultProps = {
	isSelected: false,
	hasError: false,
	onClick: noop,
	thumbnailNameSelector: defaultThumbnailNameSelector,
};
